import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { formatDateFromTimestamp } from "../utils/dateUtils";
import { useLanguage } from '../contexts/LanguageContext.jsx';
import { t, getTaxCategoryLabel } from '../utils/translations';
import { isWhitelisted, normalizeSymbol, isRelevantPair } from '../utils/exchangeImporter.js';
import { buildUniqueTaxRowKeys } from '../utils/taxRowKeys.js';
import FifoDetailRow from './FifoDetailRow'; // 👈 NEU: Für aufklappbare FIFO-Details
import { FileSpreadsheet, LayoutDashboard } from "lucide-react";
import { cn, tableTheme as T, analysisTone } from "./ui/analysisTableTheme.js";
import UnifiedTableEmptyState, { computeTableEmptyMetrics } from "./UnifiedTableEmptyState.jsx";
import {
  getTaxYearEndExclusiveTs,
  resolveDisplayTaxYearLabel,
  rowMatchesTaxYear,
  toUnixSecondsLoose,
} from "../utils/taxYear.js";
import { fmtTokenAmountFixed, fmtEurRow, fmtEurSummary } from "../utils/formatNumbers";
import MaskedEurCell from "./MaskedEurCell";
import {
  backendCurrencyOrFallback,
  currencySymbolOf,
  firstFinite,
  pickFiatTaxable,
} from "../utils/fiatFields";
import {
  shouldMaskPreviewEurRow,
  shouldShowPreviewSeparatorAfterRow,
} from "../utils/previewMode";
import PreviewTableNotice from "./PreviewTableNotice";
import PreviewModeSeparator from "./PreviewModeSeparator";
import TaxTreatmentValue from "./TaxTreatmentValue.jsx";
import { buildExchangeEventId } from "../../shared/exchangeEventId.js";

const EXCHANGE_INCOME_KINDS = new Set([
  "income",
  "reward",
  "staking",
  "airdrop",
  "interest",
]);

const isExchangeIncomeKind = (kind) =>
  EXCHANGE_INCOME_KINDS.has(String(kind || "").trim().toLowerCase());

const HeaderIconBadge = ({ children, className = "" }) => (
  <div className={cn(T.headerMediaBadge, className)}>
    <div className="absolute inset-0 bg-gradient-to-br from-white via-white/85 to-slate-50/80" />
    <div className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
      <div className="absolute -top-2 -left-3 h-5 w-12 rotate-12 bg-white/50 blur-md" />
    </div>
    <div className="relative z-10 flex items-center justify-center">
      {children}
    </div>
  </div>
);

function getExchangeRowTimestampSec(row = {}) {
  const rawTs = row?.timestamp ?? row?.ts ?? row?.date ?? row?.time ?? 0;
  const numericTs = toUnixSecondsLoose(rawTs);
  if (numericTs) return numericTs;

  if (typeof rawTs === "string") {
    const parsedMs = Date.parse(rawTs);
    if (Number.isFinite(parsedMs) && parsedMs > 0) {
      return Math.floor(parsedMs / 1000);
    }
  }

  return 0;
}

// Helper für Datumsformat
function formatDateTime(ts, language='de') {
  if (!ts) return '—';
  const d = new Date(Number(ts) * 1000);
  const locale = language === 'en' ? 'en-US' : 'de-DE';
  return d.toLocaleString(locale, {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
}


// Helper: Ist dieser Trade für FIFO-Details im PDF wichtig?
function isImportantFifoTx(row, taxRow) {
  const kind = String(row.kind || row.type || '').toLowerCase();
  const side = String(row.side || '').toUpperCase();
  const hasFifo = Array.isArray(taxRow?.fifoLegs) && taxRow.fifoLegs.length > 0;

  return kind === 'trade' && side === 'SELL' && hasFifo;
}

// 🚀 Helper: Eindeutigen, stabilen Row-Key generieren
function getRowIdentity(row) {
  // best case: Backend liefert eine eindeutige ID
  if (row.rowId) return String(row.rowId);
  if (row.importUid || row.import_uid) return String(row.importUid || row.import_uid);
  if (row.event_id) return `${row.event_id}`;
  if (row.logIndex != null && row.txid) return `${row.txid}-${row.logIndex}`;

  // fallback: so breit wie möglich (möglichst stabil und unique) — account inklusive
  return [
    row.txid || "notxid",
    row.source || "nosource",
    row.exchange_account_id || row.exchangeAccountId || row.account_id || "default",
    row.kind || row.type || "nokind",
    row.base || "nobase",
    row.timestamp || "nots",
  ].join("|");
}


function getExchangeTaxEventId(row) {
  return buildExchangeEventId(row, row.ts ?? row.timestamp);
}

function getDisplayPricingMeta(row, taxByKey, currencyCode) {
  const rawQuote = normalizeSymbol(row.quote);
  const kindLc = String(row.kind || row.type || "").toLowerCase();
  const taxKey = `${String(row.base || "").toUpperCase()}|${getExchangeTaxEventId(row)}`;
  const taxRow = taxByKey?.get(taxKey);

  const rawCsvPrice = Number(row.price);
  const hasRawCsvPrice = Number.isFinite(rawCsvPrice) && rawCsvPrice > 0;

  // Für Income verwenden wir immer die steuerliche, backend-normalisierte Bewertung
  const valuationPrice = Number(taxRow?.valuationPrice ?? 0);
  const hasValuationPrice = Number.isFinite(valuationPrice) && valuationPrice > 0;

  const useTaxValuation = kindLc === "income" && hasValuationPrice;

  const displayPrice = useTaxValuation
    ? valuationPrice
    : hasRawCsvPrice
      ? rawCsvPrice
      : null;

  const displayQuote = useTaxValuation
    ? String(taxRow?.priceCurrency || currencyCode || "EUR").toUpperCase()
    : rawQuote;

  const displayPriceSource = useTaxValuation
    ? String(taxRow?.valuationPriceSource || "tax_valuation")
    : hasRawCsvPrice
      ? "csv_raw"
      : null;

  return {
    taxKey,
    taxRow,
    displayPrice,
    displayPriceSource,
    displayQuote,
    rawPrice: hasRawCsvPrice ? rawCsvPrice : null,
    rawQuote,
  };
}

export default function ExchangeImportsTable({
  datasetId,
  year,
  displayTaxYear,
  exchangeImports = [],
  exchangeTotals = null,     // 👈 NEU: Steuerliche Totals für Footer
  exchangeTaxRows = [],      // 👈 NEU: Tax-Rows mit FIFO-Legs für Details
  selectedCountry = 'DE',    // 👈 NEU: Länderauswahl für Tax-Labels
  onDataReady,
  pdfExportMode = false,
  isPreviewMode = false,
  previewMaskedTotals = null,
}) {
  // 🌐 Sprachverwaltung
  const { language } = useLanguage();
  const yearLabel = resolveDisplayTaxYearLabel(year, selectedCountry, displayTaxYear);
  const reportEndExclusiveTs = useMemo(
    () => getTaxYearEndExclusiveTs(selectedCountry, year),
    [selectedCountry, year]
  );

  const currencyCode = backendCurrencyOrFallback(selectedCountry, exchangeTotals);
  const currencySymbol = currencySymbolOf(currencyCode);

  const exchangeDisposalNet = firstFinite(
    exchangeTotals?.disposalNetFiat,
    exchangeTotals?.disposalNetEUR,
    exchangeTotals?.disposalProfitEUR,
    0
  ) ?? 0;
  const exchangeOtherIncome = firstFinite(
    exchangeTotals?.otherIncomeFiat,
    exchangeTotals?.otherIncomeEUR,
    exchangeTotals?.incomeFiat,
    exchangeTotals?.incomeEUR,
    0
  ) ?? 0;
  const tone = analysisTone.emerald;
  const footerTone = analysisTone.indigo;
  const primaryTableWrapClass = "v3-primary-table-shell v3-primary-table-shell--emerald";
  const primaryTableHeadClass = "v3-primary-table-head v3-primary-table-head--emerald";

  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("ALL");
  const [kindFilter, setKindFilter] = useState("ALL");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null); // 👈 NEU: Toggle für FIFO-Details

  // Stabiles leeres Array (verhindert useEffect-Endlosschleife bei `|| []` pro Render)
  const emptyImportsRef = useRef([]);
  const activeData =
    Array.isArray(exchangeImports) && exchangeImports.length > 0
      ? exchangeImports
      : emptyImportsRef.current;
  
  // 🔒 Sicherheitsnetz: Finale VeChain-Whitelist-Filterung
  const displayRows = useMemo(() => {
    const filtered = (activeData || []).filter(r => {
      const rowTs = getExchangeRowTimestampSec(r);
      if (reportEndExclusiveTs != null && rowTs > 0 && rowTs >= reportEndExclusiveTs) {
        return false;
      }

      const t = String(r.kind || r.type || '').toLowerCase();
      if (['trade','spot','order','fill'].includes(t)) {
        const baseWhitelisted = isWhitelisted(r.base);
        const feeWhitelisted = isWhitelisted(r.feeAsset);
        return baseWhitelisted || feeWhitelisted;
      }
      const baseWhitelisted = isWhitelisted(r.base);
      const isFiat = ['EUR','USD'].includes(normalizeSymbol(r.base));
      const feeWhitelisted = isWhitelisted(r.feeAsset);
      return (baseWhitelisted && !isFiat) || feeWhitelisted;
    }).map(r => ({
      ...r,
      base: normalizeSymbol(r.base),
      quote: normalizeSymbol(r.quote),
      feeAsset: normalizeSymbol(r.feeAsset),
    }));
    
    return filtered;
  }, [activeData, reportEndExclusiveTs]);

  const isRowInSelectedYear = useCallback((row) => {
    const rowTs = getExchangeRowTimestampSec(row);
    if (!rowTs) return false;
    return rowMatchesTaxYear({ timestamp: rowTs }, selectedCountry, year);
  }, [selectedCountry, year]);

  // PDF: pdfRows + filtered sind useMemo — kein useEffect-Race beim Drucken
  const pdfRows = useMemo(() => {
    if (!pdfExportMode) return displayRows;

    return (displayRows || []).filter((row) => {
      const kind = String(row.kind || row.type || "").toLowerCase();
      const side = String(row.side || "").toUpperCase();

      const isTrade =
        kind === "trade" || kind === "spot" || kind === "order" || kind === "fill";
      const isBuy = isTrade && side === "BUY";
      const isSell = isTrade && side === "SELL";
      const isIncomeLike = isExchangeIncomeKind(kind);

      // Appendix 1 soll keine reine Exchange-Bewegung enthalten
      const isPureMovement =
        kind === "deposit" || kind === "withdrawal" || kind === "transfer";

      if (isPureMovement) return false;

      // Im Calculation Report nur steuerjahrbezogene Exchange-Zeilen
      if (!isRowInSelectedYear(row)) return false;

      // BUYs bleiben als Anschaffungs-/Trade-Kontext sichtbar,
      // SELLs sind steuerliche Disposals, Income bleibt Income.
      return isBuy || isSell || isIncomeLike;
    });
  }, [displayRows, isRowInSelectedYear, pdfExportMode]);

  // 📊 Tax-Row Lookup Map: Verknüpfe SELL-Trades mit ihren Tax-Rows (inkl. FIFO-Legs)
  const taxByKey = useMemo(() => {
    const map = new Map();
    for (const taxRow of exchangeTaxRows) {
      // Key: TOKEN|ex:<vendor>:<accountId>:<txRef>
      const key = `${String(taxRow.token || '').toUpperCase()}|${taxRow.event_id || taxRow.hash || ''}`;
      map.set(key, taxRow);
    }
    return map;
  }, [exchangeTaxRows]);


  const onDataReadyRef = useRef(onDataReady);
  onDataReadyRef.current = onDataReady;

  const exportableData = useMemo(() => {
    if (isPreviewMode || pdfExportMode || !Array.isArray(displayRows) || displayRows.length === 0) {
      return null;
    }

    return displayRows
      .map((row) => {
        const pricingMeta = getDisplayPricingMeta(row, taxByKey, currencyCode);

        return {
          source: row.source || "",
          kind: row.kind || "",
          timestamp: row.timestamp,
          base: row.base || "",
          quote: pricingMeta.displayQuote || "",
          side: row.side || "",
          amount: row.amount,
          quoteAmount: row.quoteAmount ?? row.quote_amount ?? null,
          price: pricingMeta.displayPrice ?? null,
          priceSource: pricingMeta.displayPriceSource ?? "",
          fee: row.fee,
          feeAsset: row.feeAsset || "",
          txid: row.txid || "",
          notes: row.notes || "",
          exchange_account_id:
            row.exchange_account_id ||
            row.exchangeAccountId ||
            row.account_id ||
            "default",
          exchangeAccountId:
            row.exchangeAccountId ||
            row.exchange_account_id ||
            row.account_id ||
            "default",
          importUid: row.importUid || row.import_uid || null,
          sourceAccountId: row.sourceAccountId || row.source_account_id || null,
          targetAccountId: row.targetAccountId || row.target_account_id || null,
          rawQuote: pricingMeta.rawQuote || "",
          rawPrice: pricingMeta.rawPrice ?? null,
          currency: currencyCode,
          date: row.timestamp
            ? new Date(row.timestamp * 1000)
                .toLocaleString("de-DE", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })
                .replace(",", "")
            : "",
        };
      })
      .sort((a, b) => {
        let aDate = 0;
        let bDate = 0;

        if (a.date) {
          if (a.date.match(/^\d{2}\.\d{2}\.\d{4}/)) {
            const [datePart, timePart] = a.date.split(" ");
            const [day, month, year] = datePart.split(".");
            const [hour, minute] = (timePart || "00:00").split(":");
            aDate = new Date(year, month - 1, day, hour, minute).getTime();
          } else if (a.date !== "") {
            aDate = new Date(a.date).getTime();
          }
        }

        if (b.date) {
          if (b.date.match(/^\d{2}\.\d{2}\.\d{4}/)) {
            const [datePart, timePart] = b.date.split(" ");
            const [day, month, year] = datePart.split(".");
            const [hour, minute] = (timePart || "00:00").split(":");
            bDate = new Date(year, month - 1, day, hour, minute).getTime();
          } else if (b.date !== "") {
            bDate = new Date(b.date).getTime();
          }
        }

        return bDate - aDate;
      });
  }, [displayRows, taxByKey, currencyCode, isPreviewMode, pdfExportMode]);

  const lastExportSigRef = useRef("");

  useEffect(() => {
    if (!exportableData) return;

    const sig = `${exportableData.length}|${exportableData[0]?.txid}|${exportableData[0]?.timestamp}|${exportableData[exportableData.length - 1]?.txid}`;
    if (lastExportSigRef.current === sig) return;
    lastExportSigRef.current = sig;

    onDataReadyRef.current?.(exportableData);
  }, [exportableData]);

  // 🚀 Reset Expand-State bei Filter-Wechsel
  useEffect(() => {
    setExpandedKey(null);
  }, [sourceFilter, kindFilter, search]);

  const filtered = useMemo(() => {
    return pdfRows
      .filter((row) => {
        const matchSource = sourceFilter === "ALL" || row.source === sourceFilter;

        let matchKind = false;
        if (kindFilter === "ALL") {
          matchKind = true;
        } else {
          const kind = (row.kind || "").toLowerCase();
          matchKind = kind === kindFilter.toLowerCase() || kind.includes(kindFilter.toLowerCase());
        }

        const matchSearch =
          search === "" ||
          [
            row.source || "",
            row.kind || "",
            row.base || "",
            row.quote || "",
            row.side || "",
            String(row.amount || ""),
            String(row.price || ""),
            String(row.fee || ""),
            row.feeAsset || "",
            row.txid || "",
            row.notes || "",
          ].some((field) => field.toLowerCase().includes(search.toLowerCase()));

        return matchSource && matchKind && matchSearch;
      })
      .sort((a, b) => {
        const aTimestamp = a.timestamp || 0;
        const bTimestamp = b.timestamp || 0;
        return bTimestamp - aTimestamp;
      });
  }, [pdfRows, sourceFilter, kindFilter, search]);

  const rowKeys = useMemo(
    () => buildUniqueTaxRowKeys(filtered, getRowIdentity),
    [filtered]
  );

  const emptyMetrics = useMemo(
    () =>
      computeTableEmptyMetrics({
        allRows: activeData,
        yearRows: pdfRows,
        filteredRows: filtered,
        effectiveYear: String(year ?? ""),
      }),
    [activeData, pdfRows, filtered, year]
  );

  // Helper function für Side-Anzeige
  function renderSideCell(side) {
    if (!side) return "—";

    const s = String(side).toUpperCase();
    if (pdfExportMode) {
      return <span className="text-black">{s}</span>;
    }

    const base = "inline-flex items-center justify-center whitespace-nowrap text-xs font-semibold";
    const screenBase = "px-3 py-1 rounded-full border shadow-sm";
    const screenTheme =
      s === "BUY"
        ? "bg-emerald-50 text-emerald-800 border-emerald-200 ring-1 ring-emerald-100/60"
        : "bg-rose-50 text-rose-800 border-rose-200 ring-1 ring-rose-100/60";

    return (
      <span className={`${base} ${screenBase} ${screenTheme}`}>
        {s}
      </span>
    );
  }

  // Helper function für Source-Anzeige - BITPANDA als Button bei SELL-Trades
  function renderSourceCell(source, hasFifoButton = false, isExpanded = false, onToggle = null) {
    const base = "inline-flex items-center gap-1.5 whitespace-nowrap text-[11px]";
    const screenBadge = "px-3 py-1 rounded-full border font-semibold bg-blue-50 text-blue-800 border-blue-200 shadow-sm";

    if (pdfExportMode) {
      return <span className="text-black">{source || "—"}</span>;
    }

    if (hasFifoButton && !pdfExportMode) {
      // Button nur im Screen-Modus
      return (
        <button
          onClick={onToggle}
          className={`${base} ${screenBadge} hover:bg-blue-100 transition-colors duration-200 cursor-pointer`}
          title={language === "en" ? "Show FIFO details" : "FIFO-Details anzeigen"}
        >
          <span className="text-xs">{isExpanded ? "▼" : "▶"}</span>
          {source || "—"}
        </button>
      );
    }

    return (
      <span className={`${base} ${screenBadge}`}>
        {source || "—"}
      </span>
    );
  }

  // Helper function für Kind-Anzeige
  function renderKindCell(kind) {
    const key = String(kind || "").toLowerCase() || "other";

    if (pdfExportMode) {
      return <span className="text-black">{kind || "—"}</span>;
    }

    const base = "inline-flex items-center justify-center whitespace-nowrap text-xs font-semibold";
    const screenBase = "px-3 py-1 rounded-full border shadow-sm";

    const themes = {
      trade: {
        screen: "bg-emerald-50 text-emerald-800 border-emerald-200 ring-1 ring-emerald-100/60",
      },
      deposit: {
        screen: "bg-sky-50 text-sky-800 border-sky-200 ring-1 ring-sky-100/60",
      },
      withdrawal: {
        screen: "bg-amber-50 text-amber-900 border-amber-200 ring-1 ring-amber-100/60",
      },
      income: {
        screen: "bg-violet-50 text-violet-800 border-violet-200 ring-1 ring-violet-100/60",
      },
      fee: {
        screen: "bg-rose-50 text-rose-800 border-rose-200 ring-1 ring-rose-100/60",
      },
      airdrop: {
        screen: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200 ring-1 ring-fuchsia-100/60",
      },
      interest: {
        screen: "bg-indigo-50 text-indigo-800 border-indigo-200 ring-1 ring-indigo-100/60",
      },
      other: {
        screen: "bg-gray-50 text-gray-800 border-gray-200 ring-1 ring-gray-100/60",
      },
    };

    const t = themes[key] || themes.other;

    return (
      <span className={`${base} ${screenBase} ${t.screen}`}>
        {kind || "—"}
      </span>
    );
  }

  function renderPriceValue(price, source, maskRowEur = false) {
    if (maskRowEur) {
      return (
        <div className="flex justify-center">
          <MaskedEurCell
            iconOnly
            dense
            currencySymbol={currencySymbol}
            currencyCode={currencyCode}
            language={language}
          />
        </div>
      );
    }

    if (!price) {
      return <span className="text-gray-400">—</span>;
    }

    let badge = null;
    let tooltip = "";

    if (source === 'local_index_fallback') {
      badge = 'IDX';
      tooltip = language === 'en'
        ? 'Local historical valuation price used'
        : 'Lokaler historischer Bewertungspreis verwendet';
    } else if (source === 'csv_converted_to_fiat') {
      badge = 'FX';
      tooltip = language === 'en'
        ? 'CSV price converted into analysis currency'
        : 'CSV-Preis wurde in die Analysewährung umgerechnet';
    } else if (source === 'csv_fiat' || source === 'csv_raw' || source === 'csv') {
      badge = 'CSV';
      tooltip = language === 'en'
        ? 'Price from exchange CSV'
        : 'Preis aus der Exchange-CSV';
    }

    return (
      <div
        className={
          pdfExportMode
            ? "text-black"
            : "inline-flex items-center justify-center min-w-[92px] gap-1.5"
        }
      >
        <span className={pdfExportMode ? "text-black" : "font-mono text-gray-900"}>
          {Number(price).toFixed(8)}
        </span>

        {badge && !pdfExportMode && (
          <span
            title={tooltip}
            className="inline-flex items-center rounded border border-gray-200 bg-gray-50/80 px-1.5 py-[1px] text-[9px] font-medium leading-none text-gray-500"
          >
            {badge}
          </span>
        )}
      </div>
    );
  }

  // Get unique sources for filter
  const uniqueSources = [...new Set(pdfRows.map(row => row.source))].filter(Boolean);
  const uniqueKinds = [...new Set(pdfRows.map(row => row.kind))].filter(Boolean);

  const statusBanner =
    "mx-6 mt-4 rounded-2xl border px-4 py-3 text-sm shadow-sm";

  const renderExchangeThead = () => (
    <thead className={cn(T.thead, primaryTableHeadClass)}>
              <tr>
                <th className={cn(T.th, "text-center w-[10%]")}>
                  <span className={T.thInnerCenter}>
                    <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("exchange", language)}</span>
                  </span>
                </th>

                <th className={cn(T.th, "text-center w-[8%]")}>
                  <span className={T.thInnerCenter}>
                    <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("exchangeType", language) || t("type", language)}</span>
                  </span>
                </th>

                <th className={cn(T.th, "text-center w-[16%]")}>
                  <span className={T.thInnerCenter}>
                    <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("txHashColumn", language)}</span>
                  </span>
                </th>

                <th className={cn(T.th, "text-center w-[8%]")}>
                  <span className={T.thInnerCenter}>
                    <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("token", language)}</span>
                  </span>
                </th>

                <th className={cn(T.th, "text-center w-[8%]")}>
                  <span className={T.thInnerCenter}>
                    <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("quote", language)}</span>
                  </span>
                </th>

                <th className={cn(T.th, "text-center w-[8%]")}>
                  <span className={T.thInnerCenter}>
                    <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("side", language)}</span>
                  </span>
                </th>

                <th className={cn(T.th, "text-center w-[10%]")}>
                  <span className={T.thInnerCenter}>
                    <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("amount", language)}</span>
                  </span>
                </th>

                <th className={cn(T.th, "text-center w-[10%]")}>
                  <span className={T.thInnerCenter}>
                    <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("price", language)}</span>
                    <span className={cn(T.thSub, "v3-th-sub-fix")}>{currencyCode}</span>
                  </span>
                </th>

                <th className={cn(T.th, "text-center w-[8%]")}>
                  <span className={T.thInnerCenter}>
                    <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("fee", language)}</span>
                  </span>
                </th>

                <th className={cn(T.th, "text-center w-[8%]")}>
                  <span className={T.thInnerCenter}>
                    <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("feeAsset", language)}</span>
                  </span>
                </th>

                <th className={cn(T.th, "text-center w-[12%]")}>
                  <span className={T.thInnerCenter}>
                    <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("taxableProfitShort", language)}</span>
                    <span className={cn(T.thSub, "v3-th-sub-fix")}>({currencyCode})</span>
                  </span>
                </th>

                <th className={cn(T.th, "text-center w-[12%]")}>
                  <span className={T.thInnerCenter}>
                    <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("date", language)}</span>
                  </span>
                </th>
              </tr>
            </thead>
  );

  const renderExchangeFooter = () =>
    filtered.length > 0 && (exchangeTotals || isPreviewMode) ? (
      <>
                    <tr className={cn("font-semibold border-t", footerTone.footerPrimary)}>
                      <td colSpan={10} className="px-6 py-4 text-right">
                        {getTaxCategoryLabel('disposal', selectedCountry, language)} ({t('exchangeSells', language)}) – {language === 'en' ? `Sum ${yearLabel}` : `Summe Steuerjahr ${yearLabel}`}:
                      </td>
                      <td className="px-6 py-4 font-mono">
                        {isPreviewMode
                          ? <MaskedEurCell currencySymbol={currencySymbol} currencyCode={currencyCode} language={language} value={previewMaskedTotals?.disposal || `${currencySymbol} **.**`} fullWidth />
                          : <>{currencySymbol} {fmtEurSummary(exchangeDisposalNet)}</>
                        }
                      </td>
                      <td className="px-6 py-4" />
                    </tr>
                    {/* Other Income-Zeile - IMMER anzeigen wie in B3trTaxTable */}
                    <tr className={cn("font-semibold border-t", footerTone.footerSecondary)}>
                      <td colSpan={10} className="px-6 py-4 text-right">
                        {getTaxCategoryLabel('other', selectedCountry, language)} ({language === 'en' ? 'Exchange income' : 'Exchange income'}) – {language === 'en' ? `Sum ${yearLabel}` : `Summe Steuerjahr ${yearLabel}`}:
                      </td>
                      <td className="px-6 py-4 font-mono">
                        {isPreviewMode
                          ? <MaskedEurCell currencySymbol={currencySymbol} currencyCode={currencyCode} language={language} value={previewMaskedTotals?.other || `${currencySymbol} **.**`} fullWidth />
                          : <>{currencySymbol} {fmtEurSummary(exchangeOtherIncome)}</>
                        }
                      </td>
                      <td className="px-6 py-4" />
                    </tr>
                    <tr className="border-b border-slate-200/80 bg-slate-50/60">
                      <td colSpan={12} className="px-6 py-2 text-xs text-slate-600 italic text-center">
                        {language === 'en' 
                          ? 'Note: Trades from other years are not included in this sum'
                          : 'Hinweis: Trades aus anderen Jahren werden nicht eingerechnet'
                        }
                      </td>
                    </tr>
      </>
    ) : null;

  const renderExchangeRow = (row, index, { virtualMode = false } = {}) => {
const maskRowEur = shouldMaskPreviewEurRow(index, filtered.length, isPreviewMode);
                  const rowKey = rowKeys[index] ?? getRowIdentity(row);
                  const isExpanded = expandedKey === rowKey;
                  
                  // 📊 Tax-Row Lookup (SELL + Income)
                  const kindLc = String(row.kind || '').toLowerCase();
                  const isSell = kindLc === 'trade' && String(row.side).toUpperCase() === 'SELL';
                  const isIncome = isExchangeIncomeKind(kindLc);
                  const {
                    taxRow,
                    displayPrice,
                    displayPriceSource,
                    displayQuote,
                  } = getDisplayPricingMeta(row, taxByKey, currencyCode);

                  const hasDetails =
                    isSell &&
                    taxRow &&
                    taxRow.fifoLegs &&
                    taxRow.fifoLegs.length > 0;

                  const showDetails = pdfExportMode
                    ? isImportantFifoTx(row, taxRow)
                    : isExpanded && hasDetails;
                  
                  const rowInner = (
                <>
                  {pdfExportMode && showDetails && (
                    <tr className="pdf-exchange-row-head">
                      <th className={cn(T.th, "text-center w-[10%]")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("exchange", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center w-[8%]")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("exchangeType", language) || t("type", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center w-[16%]")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("txHashColumn", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center w-[8%]")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("token", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center w-[8%]")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("quote", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center w-[8%]")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("side", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center w-[10%]")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("amount", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center w-[10%]")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("price", language)}</span>
                          <span className={cn(T.thSub, "v3-th-sub-fix")}>{currencyCode}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center w-[8%]")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("fee", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center w-[8%]")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("feeAsset", language)}</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center w-[12%]")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("taxableProfitShort", language)}</span>
                          <span className={cn(T.thSub, "v3-th-sub-fix")}>({currencyCode})</span>
                        </span>
                      </th>
                      <th className={cn(T.th, "text-center w-[12%]")}>
                        <span className={T.thInnerCenter}>
                          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("date", language)}</span>
                        </span>
                      </th>
                    </tr>
                  )}
                  <tr className={cn(T.tr, "pdf-tx-main-row")}>
                    {/* 1 Börse - BITPANDA als Button bei SELL-Trades */}
                    <td className="px-6 py-4 text-center">
                      {renderSourceCell(
                        row.source,
                        hasDetails,
                        showDetails,
                        () => {
                          if (pdfExportMode) return;
                          setExpandedKey(isExpanded ? null : rowKey);
                        }
                      )}
                    </td>

                  {/* 2 Art */}
                  <td className="px-6 py-4 text-center">
                    {renderKindCell(row.kind)}
                  </td>

                  {/* 3 TXID */}
                  <td
                    className="px-6 py-4 text-xs text-gray-700 text-center break-all leading-5 tracking-normal"
                    title={row.txid || ''}>
                    {row.txid ? row.txid : '—'}
                  </td>

                  {/* 4 Token (Base) */}
                  <td className="px-6 py-4 font-medium text-gray-800 text-center">
                    {row.base || '—'}
                  </td>

                  {/* 5 Quote */}
                  <td className="px-6 py-4 font-medium text-gray-800 text-center">
                    {displayQuote || '—'}
                  </td>

                  {/* 6 Side */}
                  <td className="px-6 py-4 text-center">
                    {renderSideCell(row.side)}
                  </td>

                  {/* 7 Menge */}
                  <td className="px-6 py-4 font-mono text-gray-900 text-center">
                    {row.amount ? fmtTokenAmountFixed(row.amount, 6) : '—'}
                  </td>

                  {/* 8 Preis */}
                  <td className="px-6 py-4 text-center">
                    {renderPriceValue(displayPrice, displayPriceSource, maskRowEur)}
                  </td>

                  {/* 9 Gebühr */}
                  <td className="px-6 py-4 font-mono text-gray-900 text-center">
                    {maskRowEur ? (
                      <div className="flex justify-center">
                        <MaskedEurCell
                          iconOnly
                          dense
                          currencySymbol={currencySymbol}
                          currencyCode={currencyCode}
                          language={language}
                        />
                      </div>
                    ) : row.fee ? (
                      Number(row.fee).toFixed(8)
                    ) : (
                      "—"
                    )}
                  </td>

                  {/* 10 Fee-Asset */}
                  <td className="px-6 py-4 font-mono text-gray-900 text-center">
                    {row.feeAsset || '—'}
                  </td>

                  {/* 11 Steuerpflichtiger Gewinn € - immer den korrekten Wert anzeigen */}
                  <td className="px-6 py-4 text-gray-700 text-center">
                    {(isSell || isIncome) && taxRow ? (() => {
                      const taxableAmount = Number(
                        pickFiatTaxable(taxRow) ??
                        taxRow.taxableProfitEUR ??
                        taxRow.taxableProfit ??
                        taxRow.euroAmount ??
                        0
                      );
                      const isRelevantForYear = isRowInSelectedYear(row);
                      
                      return (
                        <div className="flex flex-col items-center gap-1 text-black">
                          {maskRowEur ? (
                            <div className="flex w-full justify-center px-1">
                              <MaskedEurCell
                                currencySymbol={currencySymbol}
                                currencyCode={currencyCode}
                                language={language}
                                fullWidth
                              />
                            </div>
                          ) : (
                            <TaxTreatmentValue
                              row={taxRow}
                              language={language}
                              selectedCountry={selectedCountry}
                              treatment={isIncome ? "income" : "disposal"}
                              formattedValue={`${currencySymbol} ${fmtEurRow(taxableAmount)}`}
                            />
                          )}
                          {!isRelevantForYear && (
                            <span className="text-[10px] text-gray-500 italic print:text-black">
                              {language === "en" ? `Not in ${yearLabel} sum` : `Nicht in ${yearLabel} Summe`}
                            </span>
                          )}
                        </div>
                      );
                    })() : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>

                  {/* 12 Datum */}
                  <td className="px-6 py-4 text-gray-700 text-center">
                    {formatDateTime(row.timestamp, language)}
                    </td>
                  </tr>

                  {/* 📊 FIFO-Detail-Row bei SELL-Trades (aufklappbar) - spannt alle 12 Spalten */}
                  {hasDetails && showDetails && (
                          <FifoDetailRow
                            tx={taxRow}
                      colSpan={12}
                            selectedCountry={selectedCountry}
                            selectedYear={Number(year)}
                            language={language}
                            isPreviewMode={isPreviewMode}
                            maskRowEur={maskRowEur}
                            pdfExportMode={pdfExportMode}
                          />
                  )}
                  <PreviewModeSeparator
                    show={shouldShowPreviewSeparatorAfterRow(index, filtered.length, isPreviewMode)}
                    colSpan={12}
                    language={language}
                    currencyCode={currencyCode}
                  />                </>
  );

  if (!virtualMode) {
    return (
      <tbody key={rowKey} className={cn("pdf-row-group", T.tbody)}>
        {rowInner}
      </tbody>
    );
  }
  return (
    <React.Fragment key={rowKey}>
      {rowInner}
    </React.Fragment>
  );
  };



  return (
    <section
      className={cn(
        T.shell,
        pdfExportMode && "rounded-none border-0 bg-white shadow-none ring-0"
      )}
    >
      <div className={cn(T.header, T.headerRail, tone.rail)}>
        <div className={T.headerInner}>
          <div className={T.headerMain}>
            <span className={T.eyebrow}>
              <LayoutDashboard className={cn("h-3.5 w-3.5", tone.eyebrow)} />
              {language === "en" ? "Exchange overview" : "Börsenübersicht"}
            </span>

            <h2 className={T.title}>
              {language === "en" ? "Exchange Imports" : "Börsen-Importe"}
            </h2>

            <p className={T.subtitle}>
              {pdfExportMode
                ? language === "en"
                  ? "Imported trades, deposits and rewards for the selected tax year."
                  : "Importierte Trades, Einzahlungen und Rewards für das ausgewählte Steuerjahr."
                : language === "en"
                  ? "Imported trades, deposits and rewards through the end of the selected tax year."
                  : "Importierte Trades, Einzahlungen und Rewards bis zum Ende des ausgewählten Steuerjahres."}
            </p>
          </div>

          {!pdfExportMode && (
            <div className={T.headerAsidePills}>
              <span className={cn(T.headerChipSoft, "text-emerald-700 border-emerald-200 bg-emerald-50/80")}>
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                <span>{language === "en" ? "CSV / API" : "CSV / API"}</span>
              </span>

              <HeaderIconBadge className="print:bg-white print:shadow-none print:ring-gray-300">
                <FileSpreadsheet className="h-7 w-7 text-emerald-600" strokeWidth={2.2} />
              </HeaderIconBadge>
            </div>
          )}
        </div>
      </div>

      {!pdfExportMode && (
        <div className={cn(T.toolbarWrap, "print:hidden")}>
          <div className={cn(T.toolbar, "flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between")}>
            <div className="flex-1 min-w-0">
              <input
                type="text"
                placeholder={`🔍 ${language === "en" ? "Search source, token, amount, txid..." : "Suche Quelle, Token, Betrag, TxID..."}`}
                className={T.input}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {filtered.length > 0 && !loading ? (
                <span className={T.countPill}>
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  <span className="font-semibold text-slate-800">{filtered.length}</span>
                  <span>{language === "en" ? "transactions" : "Transaktionen"}</span>
                </span>
              ) : null}

              <select className={T.select} value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                <option value="ALL">🔁 {language === "en" ? "All Sources" : "Alle Quellen"}</option>
                {uniqueSources.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </select>

              <select className={T.select} value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
                <option value="ALL">🔎 {language === "en" ? "All Types" : "Alle Typen"}</option>
                <option value="trade">🔄 {language === "en" ? "Trade" : "Handel"}</option>
                <option value="deposit">⬇️ {language === "en" ? "Deposit" : "Einzahlung"}</option>
                <option value="withdrawal">⬆️ {language === "en" ? "Withdrawal" : "Auszahlung"}</option>
                <option value="income">💎 {language === "en" ? "Income / Rewards" : "Einnahmen / Belohnungen"}</option>
                <option value="fee">💸 {language === "en" ? "Fee" : "Gebühr"}</option>
                <option value="airdrop">🎁 {language === "en" ? "Airdrop" : "Airdrop"}</option>
                <option value="interest">💰 {language === "en" ? "Interest" : "Zinsen"}</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className={cn(statusBanner, "border-emerald-200 bg-emerald-50/80 text-emerald-800")}>
          ⏳ {language === "en" ? "Loading exchange data..." : "Lade Börsendaten..."}
        </div>
      )}
      {error && (
        <div className={cn(statusBanner, "border-rose-200 bg-rose-50/80 text-rose-800")}>
          ❌ {error}
        </div>
      )}
      {/* Tabelle */}
      {!loading && !error && (
        <>
        <PreviewTableNotice
          isPreviewMode={isPreviewMode}
          totalRows={filtered.length}
          language={language}
          currencyCode={currencyCode}
        />

        <div className={cn(T.tableBlock, "hidden md:block print:block")}>
          <div className={cn(T.tableWrap, primaryTableWrapClass)}>
          {filtered.length === 0 ? (
            <table
              className={cn(
                T.table,
                "min-w-[1240px] table-fixed print:min-w-0 print:table-auto"
              )}
            >
              {renderExchangeThead()}
              <tbody className={T.tbody}>
                <tr>
                  <td colSpan={12} className="px-6 py-10">
                    {!pdfExportMode ? (
                      <UnifiedTableEmptyState
                        language={language}
                        year={String(yearLabel ?? "")}
                        scopeLabel={language === "en" ? "Exchange" : "Börse"}
                        totalCount={emptyMetrics.totalCount}
                        yearCount={emptyMetrics.yearCount}
                        otherYearsCount={emptyMetrics.otherYearsCount}
                        hiddenByFilterCount={emptyMetrics.hiddenByFilterCount}
                        inTable
                      />
                    ) : null}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <table
              className={cn(
                T.table,
                "min-w-[1240px] table-fixed print:min-w-0 print:table-auto"
              )}
            >
              {renderExchangeThead()}
              {filtered.map((row, index) => renderExchangeRow(row, index))}
              {renderExchangeFooter() ? (
                <tbody>{renderExchangeFooter()}</tbody>
              ) : null}
            </table>
          )}
          </div>
        </div>

        </>
      )}
    </section>
  );
}
